import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Typography,
  Box,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import {
  CloudUploadOutlined as CloudUploadIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon
} from '@material-ui/icons'
import { Toast } from 'components/shared/Toaster'
import Loader from 'components/shared/Loader'
import strings from 'strings'
import Button from '../../../../shared/Button'
import StatementComparison from './StatementComparison'
import { deriveStatementVerdict, formatPeriodLabel } from './bankDocsAdapters'
import {
  getBankDocStatus,
  getLastBankDoc,
  getPreviousBankDoc,
  uploadBankDoc,
  requestBankDocComparison,
  getBankDocComparison
} from '../../../../../actions/applicants'
import { findCreditCost, sortDocsByDate} from 'tools/helper'
import CreditCost from '../../../../shared/CreditCost'
import {Icon} from 'styles'
const STATUS_STYLES = {
  'AT RISK': { accent: 'var(--status-pending)', chipBg: 'rgba(232,115,74,0.16)', chipText: 'var(--status-pending)' },
  WATCH: { accent: '#9b8afb', chipBg: 'rgba(155,138,251,0.18)', chipText: '#9b8afb' },
  HEALTHY: { accent: '#4caf82', chipBg: 'rgba(76,175,130,0.16)', chipText: '#4caf82' }
}

const BANNER_BG = '#152420'
const GRAY_BAR = '#d8dbdd'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    backgroundColor: 'white',
    minHeight: '400px'
  },
  centeredState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    textAlign: 'center'
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
  },
  sidebar: {
    width: '100%'
  },
  sidebarCard: {
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none'
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5)
  },
  sidebarHeaderLeft: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1)
  },
  sidebarActions: {
    display: 'flex',
    gap: theme.spacing(1.5)
  },
  sidebarLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--neutral-400)',
    letterSpacing: '0.05em'
  },
  statementsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5)
  },
  statementItem: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: '1 1 220px',
    minWidth: '200px',
    padding: theme.spacing(1.5),
    borderRadius: '10px',
    border: '1px solid #ECECEC',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease'
  },
  statementItemActive: {
    borderColor: '#9b8afb',
    backgroundColor: 'rgba(155,138,251,0.06)',
    borderLeft: '3px solid #9b8afb'
  },
  statementItemEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '1 1 220px',
    minWidth: '200px',
    padding: theme.spacing(1.5),
    borderRadius: '10px',
    border: '1px dashed #ECECEC',
    color: 'var(--neutral-400)',
    fontSize: '13px'
  },
  statementBar: {
    height: '3px',
    borderRadius: '2px',
    backgroundColor: '#EFEFEF',
    marginTop: theme.spacing(1),
    overflow: 'hidden'
  },
  statementBarFill: {
    height: '100%',
    borderRadius: '2px'
  },
  content: {
    flex: 1,
    minWidth: 0
  },
  banner: {
    backgroundColor: BANNER_BG,
    color: '#fff',
    borderRadius: '14px',
    padding: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  bannerEyebrow: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    marginBottom: theme.spacing(1)
  },
  bannerHeadline: {
    fontSize: '26px',
    fontWeight: 700,
    marginBottom: theme.spacing(1.5)
  },
  bannerDescription: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.75)',
    maxWidth: '640px',
    lineHeight: 1.6
  },
  bannerScoreCol: {
    textAlign: 'right',
    flexShrink: 0,
    minWidth: '160px'
  },
  bannerScoreLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.55)',
    letterSpacing: '0.05em'
  },
  bannerScoreValue: {
    fontSize: '34px',
    fontWeight: 700,
    lineHeight: 1.1
  },
  bannerScoreUnit: {
    fontSize: '15px',
    color: 'rgba(255,255,255,0.55)'
  },
  bannerChip: {
    fontWeight: 700,
    fontSize: '11px',
    borderRadius: '8px',
    marginTop: theme.spacing(0.5)
  },
  dotStrip: {
    display: 'flex',
    gap: '3px',
    marginTop: theme.spacing(1),
    justifyContent: 'flex-end'
  },
  dot: {
    width: '10px',
    height: '5px',
    borderRadius: '3px',
    backgroundColor: 'rgba(255,255,255,0.16)'
  },
  statCardsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  statCardCol: {
    flex: '1 1 170px',
    minWidth: '170px'
  },
  statCardColWide: {
    flex: '2 1 260px',
    minWidth: '220px'
  },
  twoColRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3)
  },
  colNarrow: {
    flex: '1 1 320px',
    minWidth: '280px'
  },
  colWide: {
    flex: '2 1 480px',
    minWidth: '360px'
  },
  statCard: {
    padding: theme.spacing(2),
    height: '100%',
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none'
  },
  statCardWarning: {
    borderColor: 'var(--status-pending)'
  },
  statCardDark: {
    backgroundColor: BANNER_BG,
    color: '#fff'
  },
  statLabel: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: 'var(--neutral-400)'
  },
  statLabelDark: {
    color: 'rgba(255,255,255,0.55)'
  },
  statValue: {
    fontSize: '22px',
    fontWeight: 700,
    marginTop: theme.spacing(0.5)
  },
  statSubtext: {
    fontSize: '12px',
    marginTop: theme.spacing(0.5),
    color: 'var(--neutral-400)'
  },
  sectionCard: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none',
    height: '100%'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700
  },
  sectionSubtitle: {
    fontSize: '13px',
    color: 'var(--neutral-400)',
    marginTop: theme.spacing(0.25)
  },
  waterfallRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(3),
    padding: theme.spacing(0, 1)
  },
  waterfallCol: {
    flex: '1 1 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  waterfallPlotArea: {
    position: 'relative',
    width: '100%',
    height: '220px'
  },
  waterfallValue: {
    position: 'absolute',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: 700,
    whiteSpace: 'nowrap'
  },
  waterfallBar: {
    position: 'absolute',
    left: '15%',
    right: '15%',
    borderRadius: '4px'
  },
  runwayCallout: {
    backgroundColor: '#FCEFE9',
    borderRadius: '10px',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    fontSize: '13px',
    lineHeight: 1.6
  },
  indicatorRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  indicatorDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginTop: '6px',
    flexShrink: 0
  },
  indicatorTitle: {
    fontSize: '14px',
    fontWeight: 700
  },
  indicatorSubtext: {
    fontSize: '13px',
    color: 'var(--fg-tertiary)'
  },
  takeawayRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 0),
    borderBottom: '1px solid #F0F0F0'
  },
  takeawayIndex: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#bdbdbd',
    minWidth: '20px'
  },
  takeawayText: {
    fontSize: '14px',
    flex: 1,
    lineHeight: 1.5
  },
}))

function splitIndicatorText(text, formatCurrency) {
  const parenMatch = text.match(/^(.*)\(([^)]+)\)\s*$/)
  if (parenMatch) {
    const title = parenMatch[1].trim()
    const rawValue = parenMatch[2].trim()
    const isPlainNumber = /^-?\d+(\.\d+)?$/.test(rawValue)
    return { title, subtext: isPlainNumber ? formatCurrency(parseFloat(rawValue)) : rawValue }
  }
  const byMatch = text.match(/^(.*?)\s+by\s+(-?\d+(\.\d+)?%.*)$/i)
  if (byMatch) {
    return { title: byMatch[1].trim(), subtext: byMatch[2].trim() }
  }
  const colonMatch = text.match(/^([^:]+):\s*(.+)$/)
  if (colonMatch) {
    return { title: colonMatch[1].trim(), subtext: colonMatch[2].trim() }
  }
  return { title: text, subtext: null }
}

function isWarningIndicator(text) {
  return /(negative|decrease|zero|risk|concentration|high )/i.test(text)
}

const formatCurrency = (amount) => {
  const numAmount = Number(amount) || 0
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount)
}

const StatementInsights = ({ applicationId, creditCosts }) => {
  const classes = useStyles()

  const [initialLoading, setInitialLoading] = useState(true)
  const [status, setStatus] = useState('NOT_STARTED')
  const [ableToCompare, setAbleToCompare] = useState(false)
  const [comparisonStatus, setComparisonStatus] = useState('NOT_STARTED')
  const [currentDoc, setCurrentDoc] = useState(null)
  const [previousDoc, setPreviousDoc] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [viewingPrevious, setViewingPrevious] = useState(false)
  const [viewMode, setViewMode] = useState('statement')
  const [comparisonData, setComparisonData] = useState(null)
  const [isLoadingComparison, setIsLoadingComparison] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const documentAnalysisCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_ANALYSIS')
  const documentComparisonCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_COMPARISON')

  const fetchDocs = useCallback(async (hasPrevious) => {
    const current = await getLastBankDoc(applicationId)().catch(() => null)

    if (!hasPrevious) {
      setCurrentDoc(current)
      setPreviousDoc(null)
      return
    }
    const previous = await getPreviousBankDoc(applicationId)().catch(() => null)
    const [sortedPrevious, sortedCurrent] = sortDocsByDate(previous, current)
    setPreviousDoc(sortedPrevious)
    setCurrentDoc(sortedCurrent)
  }, [applicationId])

  const checkStatus = useCallback(async () => {
    try {
      const res = await getBankDocStatus(applicationId)()
      const nextStatus = res?.status || 'NOT_STARTED'
      const canCompareNow = !!res?.ableToCompare
      setStatus(nextStatus)
      setAbleToCompare(canCompareNow)
      setComparisonStatus(res?.comparisonStatus || 'NOT_STARTED')
      if (nextStatus === 'COMPLETED') {
        await fetchDocs(canCompareNow)
      }
      return res
    } catch (err) {
      setStatus('NOT_STARTED')
      return null
    }
  }, [applicationId, fetchDocs])

  useEffect(() => {
    // Without an applicationId there's nothing to fetch - fall through to the
    // empty state rather than rendering anything fabricated.
    if (!applicationId) {
      setInitialLoading(false)
      return
    }

    checkStatus().finally(() => setInitialLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  useEffect(() => {
    if (!applicationId || status !== 'IN_PROGRESS') return

    const interval = setInterval(async () => {
      try {
        const res = await getBankDocStatus(applicationId)()
        const nextStatus = res?.status || 'NOT_STARTED'
        const canCompareNow = !!res?.ableToCompare
        setStatus(nextStatus)
        setAbleToCompare(canCompareNow)
        setComparisonStatus(res?.comparisonStatus || 'NOT_STARTED')
        if (nextStatus !== 'IN_PROGRESS') {
          clearInterval(interval)
          if (nextStatus === 'COMPLETED') fetchDocs(canCompareNow)
        }
      } catch (err) {
        clearInterval(interval)
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [status, applicationId, fetchDocs])

  const handleUploadStatement = async (event) => {
    const file = event.target.files[0]
    event.target.value = ''
    if (!file || !applicationId) return

    setUploading(true)
    try {
      await uploadBankDoc(applicationId, file)()
      Toast({ type: 'success', message: strings.BANK_STATEMENT_UPLOAD_SUCCESS })
      await checkStatus()
    } catch (err) {
      Toast({ type: 'error', message: strings.BANK_STATEMENT_UPLOAD_ERROR })
    } finally {
      setUploading(false)
    }
  }

  const canCompare = !!(currentDoc && previousDoc) && ableToCompare

  const handleCancelComparison = () => {
    setShowConfirmDialog(false)
  }

  const handleRequestComparison = async () => {
    if (!applicationId) return
    setShowConfirmDialog(false)
    setViewMode('comparison')
    if (comparisonData) return
    setIsLoadingComparison(true)
    try {
      let res = null
      if (comparisonStatus === 'COMPLETED') {
        res = await getBankDocComparison(applicationId)().catch(() => null)
      }
      if (!res) {
        res = await requestBankDocComparison(applicationId)()
      }
      setComparisonData(res)
    } catch (err) {
      Toast({ type: 'error', message: strings.BANK_STATEMENT_COMPARISON_FAILED })
      setViewMode('statement')
    } finally {
      setIsLoadingComparison(false)
    }
  }

  const handleClickComparison = () => {
    if (!canCompare) return
    if (isLoadingComparison) return

    if (comparisonStatus === 'NOT_STARTED' && !comparisonData) {
      setViewMode('statement')
      setShowConfirmDialog(true)
    } else {
      handleRequestComparison()
    }
  }

  const activeDoc = viewingPrevious ? previousDoc : currentDoc
  const otherDoc = viewingPrevious ? currentDoc : previousDoc
  const verdict = useMemo(
    () => (activeDoc ? deriveStatementVerdict(activeDoc, otherDoc) : null),
    [activeDoc, otherDoc]
  )
  const statusStyle = verdict ? (STATUS_STYLES[verdict.statusLabel] || STATUS_STYLES.HEALTHY) : STATUS_STYLES.HEALTHY
  const insights = activeDoc?.bankNotesInsightsData || {}
  const healthIndicators = insights.healthIndicators || []
  const takeaways = insights.takeaways || []

  const netCashFlow = activeDoc ? activeDoc.credits - activeDoc.debits : 0
  const creditDebitRatio = activeDoc && activeDoc.debits !== 0
    ? activeDoc.credits / activeDoc.debits
    : (activeDoc?.credits > 0 ? Infinity : 0)
  const balancePctChange = activeDoc && activeDoc.startingBalance !== 0
    ? ((activeDoc.endBalance - activeDoc.startingBalance) / activeDoc.startingBalance) * 100
    : 0

  const waterfallBars = useMemo(() => {
    if (!activeDoc) return []
    const bars = [
      {
        label: strings.BANK_STATEMENT_OPENING,
        value: activeDoc.startingBalance,
        rangeBottom: 0,
        rangeTop: activeDoc.startingBalance,
        color: GRAY_BAR
      },
      {
        label: strings.BANK_STATEMENT_CREDITS,
        value: activeDoc.credits,
        rangeBottom: activeDoc.startingBalance,
        rangeTop: activeDoc.startingBalance + activeDoc.credits,
        color: activeDoc.credits === 0 ? statusStyle.accent : '#152420'
      },
      {
        label: strings.BANK_STATEMENT_DEBITS,
        value: -activeDoc.debits,
        rangeBottom: activeDoc.endBalance,
        rangeTop: activeDoc.startingBalance + activeDoc.credits,
        color: '#9b8afb'
      },
      {
        label: strings.BANK_STATEMENT_CLOSING,
        value: activeDoc.endBalance,
        rangeBottom: 0,
        rangeTop: activeDoc.endBalance,
        color: GRAY_BAR
      }
    ]
    const domainMax = Math.max(1, ...bars.map((bar) => Math.max(bar.rangeBottom, bar.rangeTop)))
    return bars.map((bar) => ({
      ...bar,
      bottomPct: (bar.rangeBottom / domainMax) * 100,
      heightPct: ((bar.rangeTop - bar.rangeBottom) / domainMax) * 100
    }))
  }, [activeDoc, statusStyle])

  if (initialLoading) {
    return (
      <Box className={classes.container}>
        <Loader />
      </Box>
    )
  }

  if (uploading || status === 'IN_PROGRESS') {
    return (
      <Box className={classes.container}>
        <Box className={classes.centeredState}>
          <CircularProgress size={48} thickness={4} style={{ color: BANNER_BG, marginBottom: 24 }} />
          <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
            {strings.BANK_STATEMENT_PROCESSING_TITLE}
          </Typography>
          <Typography variant="body1" style={{ color: '#666' }}>
            {strings.BANK_STATEMENT_PROCESSING_MESSAGE}
          </Typography>
        </Box>
      </Box>
    )
  }

  if (!currentDoc) {
    return (
      <Box className={classes.container}>
        <Box className={classes.centeredState}>
          <Typography variant="h4" style={{ marginBottom: 16, fontWeight: 600 }}>
            {strings.BANK_STATEMENT_NO_STATEMENTS_TITLE}
          </Typography>
          <Typography variant="body1" style={{ marginBottom: 32, color: '#666', maxWidth: 500, lineHeight: 1.6 }}>
            {strings.BANK_STATEMENT_NO_STATEMENTS_MESSAGE}
          </Typography>
          <input
            accept=".pdf,.jpg,.jpeg,.png,.csv"
            style={{ display: 'none' }}
            id="bank-statement-upload-empty"
            type="file"
            onChange={handleUploadStatement}
          />
          <label htmlFor="bank-statement-upload-empty">
            <Button variant="contained" color="primary" size="large" component="span" disabled={uploading} style={{ padding: '12px 32px', fontSize: '1rem' }}>
              {uploading ? strings.BANK_STATEMENT_UPLOADING : strings.BANK_STATEMENT_UPLOAD_DOCUMENT} <CreditCost cost={documentAnalysisCreditCost()} />
            </Button>
          </label>
        </Box>
      </Box>
    )
  }

  const sidebar = (
    <StatementSidebar
      classes={classes}
      currentDoc={currentDoc}
      previousDoc={previousDoc}
      viewingPrevious={viewingPrevious}
      onSelect={(isPrevious) => { setViewingPrevious(isPrevious); setViewMode('statement') }}
      canCompare={canCompare}
      onCompareClick={handleClickComparison}
      onUpload={handleUploadStatement}
      uploading={uploading}
      creditCosts={creditCosts}
    />
  )

  if (viewMode === 'comparison') {
    return (
      <div className={classes.container}>
        <div className={classes.layout}>
          {sidebar}
          <div className={classes.content}>
            {isLoadingComparison ? (
              <Box className={classes.centeredState}>
                <CircularProgress size={48} thickness={4} style={{ color: BANNER_BG, marginBottom: 24 }} />
                <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
                  {strings.BANK_STATEMENT_GENERATING_COMPARISON_TITLE}
                </Typography>
                <Typography variant="body1" style={{ color: '#666' }}>
                  {strings.BANK_STATEMENT_ANALYZING_CHANGES}
                </Typography>
              </Box>
            ) : (
              <StatementComparison
                fromDoc={previousDoc}
                toDoc={currentDoc}
                fromLabel={formatPeriodLabel(previousDoc.date)}
                toLabel={formatPeriodLabel(currentDoc.date)}
                comparisonData={comparisonData}
                onBack={() => setViewMode('statement')}
                formatCurrency={formatCurrency}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.layout}>
        {sidebar}

        <div className={classes.content}>
          {insights.error && (
            <Alert severity="warning" style={{ marginBottom: 16 }}>
              {insights.error}
            </Alert>
          )}

          {/* Verdict banner */}
          <div className={classes.banner}>
            <div>
              <Typography className={classes.bannerEyebrow} style={{ color: statusStyle.accent }}>
                {verdict.eyebrow}
              </Typography>
              <Typography className={classes.bannerHeadline}>{verdict.headline}</Typography>
              <Typography className={classes.bannerDescription}>{verdict.description}</Typography>
            </div>
            <div className={classes.bannerScoreCol}>
              <Typography className={classes.bannerScoreLabel}>{strings.BANK_STATEMENT_HEALTH_SCORE}</Typography>
              <Typography className={classes.bannerScoreValue} style={{ color: statusStyle.accent }}>
                {(insights.healthScore || 0).toFixed(1)}<span className={classes.bannerScoreUnit}>/10</span>
              </Typography>
              <Chip
                label={verdict.statusLabel}
                size="small"
                className={classes.bannerChip}
                style={{ backgroundColor: statusStyle.chipBg, color: statusStyle.chipText }}
              />
              <div className={classes.dotStrip}>
                {Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className={classes.dot}
                    style={i < Math.round(insights.healthScore || 0) ? { backgroundColor: statusStyle.accent } : undefined}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Stat cards */}
          <div className={classes.statCardsRow}>
            <div className={classes.statCardCol}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statLabel}>{strings.BANK_STATEMENT_STARTING_BALANCE}</Typography>
                <Typography className={classes.statValue}>{formatCurrency(activeDoc.startingBalance)}</Typography>
                <Typography className={classes.statSubtext}>{verdict.statCardNotes.startingBalance}</Typography>
              </Paper>
            </div>
            <div className={classes.statCardCol}>
              <Paper className={`${classes.statCard} ${activeDoc.credits === 0 ? classes.statCardWarning : ''}`}>
                <Typography className={classes.statLabel}>{strings.BANK_STATEMENT_CREDITS}</Typography>
                <Typography className={classes.statValue}>{formatCurrency(activeDoc.credits)}</Typography>
                <Typography className={classes.statSubtext} style={activeDoc.credits === 0 ? { color: statusStyle.accent } : undefined}>
                  {verdict.statCardNotes.credits}
                </Typography>
              </Paper>
            </div>
            <div className={classes.statCardCol}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statLabel}>{strings.BANK_STATEMENT_DEBITS}</Typography>
                <Typography className={classes.statValue}>{formatCurrency(activeDoc.debits)}</Typography>
                <Typography className={classes.statSubtext}>{verdict.statCardNotes.debits}</Typography>
              </Paper>
            </div>
            <div className={classes.statCardCol}>
              <Paper className={classes.statCard}>
                <Typography className={classes.statLabel}>{strings.BANK_STATEMENT_NET_CASH_FLOW}</Typography>
                <Typography
                  className={classes.statValue}
                  style={{ color: netCashFlow >= 0 ? 'var(--status-approved)' : 'var(--status-pending)' }}
                >
                  {netCashFlow >= 0 ? '+' : ''}{formatCurrency(netCashFlow)}
                </Typography>
                <Typography className={classes.statSubtext}>
                  {`Credit/debit ${Number.isFinite(creditDebitRatio) ? creditDebitRatio.toFixed(2) : '—'}`}
                </Typography>
              </Paper>
            </div>
            <div className={classes.statCardColWide}>
              <Paper className={`${classes.statCard} ${classes.statCardDark}`}>
                <Typography className={`${classes.statLabel} ${classes.statLabelDark}`}>{strings.BANK_STATEMENT_ENDING_BALANCE}</Typography>
                <Typography className={classes.statValue}>{formatCurrency(activeDoc.endBalance)}</Typography>
                <Typography className={classes.statSubtext} style={{ color: balancePctChange >= 0 ? '#7fd9ab' : '#f0a988' }}>
                  {balancePctChange >= 0 ? <ArrowUpIcon style={{ fontSize: 12 }} /> : <ArrowDownIcon style={{ fontSize: 12 }} />}
                  {' '}{Math.abs(balancePctChange).toFixed(1)}% on the period
                </Typography>
              </Paper>
            </div>
          </div>

          {/* Cash movement chart */}
          <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
            <Typography className={classes.sectionTitle}>{strings.BANK_STATEMENT_CASH_MOVEMENT}</Typography>
            <Typography className={classes.sectionSubtitle}>
              {formatCurrency(activeDoc.credits)} {strings.BANK_STATEMENT_IN.toLowerCase()}. {formatCurrency(activeDoc.debits)} {strings.BANK_STATEMENT_OUT.toLowerCase()}.
            </Typography>
            <Box mt={3} className={classes.waterfallRow}>
              {waterfallBars.map((bar) => (
                <div className={classes.waterfallCol} key={bar.label}>
                  <div className={classes.waterfallPlotArea}>
                    <Typography
                      className={classes.waterfallValue}
                      style={{ bottom: `calc(${bar.bottomPct + bar.heightPct}% + 8px)` }}
                    >
                      {formatCurrency(bar.value)}
                    </Typography>
                    <div
                      className={classes.waterfallBar}
                      style={{
                        bottom: `${bar.bottomPct}%`,
                        height: `${bar.heightPct}%`,
                        minHeight: bar.heightPct === 0 ? '3px' : undefined,
                        backgroundColor: bar.color
                      }}
                    />
                  </div>
                  <Typography className={classes.statLabel} style={{ marginTop: 12 }}>{bar.label}</Typography>
                </div>
              ))}
            </Box>
            {verdict.runway && (
              <div className={classes.runwayCallout}>
                <strong>Runway.</strong> {verdict.runway}
              </div>
            )}
          </Paper>

          <div className={classes.twoColRow}>
            {/* Health indicators + fees */}
            <div className={classes.colNarrow}>
              <Paper className={classes.sectionCard}>
                <Typography className={classes.sectionTitle}>{strings.BANK_STATEMENT_HEALTH_INDICATORS}</Typography>
                <Typography className={classes.sectionSubtitle}>
                  {healthIndicators.length} {strings.BANK_STATEMENT_INDICATORS_SUBTITLE_SUFFIX}
                </Typography>
                <Box mt={2}>
                  {healthIndicators.map((indicator, index) => {
                    const { title, subtext } = splitIndicatorText(indicator, formatCurrency)
                    const warning = isWarningIndicator(indicator)
                    return (
                      <div className={classes.indicatorRow} key={index}>
                        <div className={classes.indicatorDot} style={{ backgroundColor: warning ? 'var(--status-pending)' : '#2c2c2c' }} />
                        <div>
                          <Typography className={classes.indicatorTitle}>{title}</Typography>
                          {subtext && <Typography className={classes.indicatorSubtext}>{subtext}</Typography>}
                        </div>
                      </div>
                    )
                  })}
                </Box>
                <Divider style={{ margin: '16px 0' }} />
                <Typography className={classes.statLabel}>{strings.BANK_STATEMENT_FEES_IDENTIFIED}</Typography>
                {activeDoc.feesAndCharges ? (
                  <>
                    <Typography style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{formatCurrency(activeDoc.feesAndCharges)}</Typography>
                    <Typography className={classes.statSubtext}>
                      {`${((activeDoc.feesAndCharges / activeDoc.debits) * 100).toFixed(2)}% of debits.`}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{strings.BANK_STATEMENT_NO_FEES_REPORTED}</Typography>
                    <Typography className={classes.statSubtext}>{strings.BANK_STATEMENT_NO_FEE_LINE_ITEMS}</Typography>
                  </>
                )}
              </Paper>
            </div>

            {/* Takeaways */}
            <div className={classes.colWide}>
              <Paper className={classes.sectionCard}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <div>
                    <Typography className={classes.sectionTitle}>{strings.BANK_STATEMENT_TAKEAWAYS}</Typography>
                    <Typography className={classes.sectionSubtitle}>{strings.BANK_STATEMENT_TAKEAWAYS_SUBTITLE}</Typography>
                  </div>
                </Box>
                <Box mt={2}>
                  {takeaways.map((takeaway, index) => (
                    <div className={classes.takeawayRow} key={index}>
                      <Typography className={classes.takeawayIndex}>{String(index + 1).padStart(2, '0')}</Typography>
                      <Typography className={classes.takeawayText}>{takeaway}</Typography>
                    </div>
                  ))}
                </Box>
              </Paper>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Comparison */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelComparison}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {strings.BANK_STATEMENT_GENERATE_COMPARISON_TITLE}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {strings.BANK_STATEMENT_GENERATE_COMPARISON_MESSAGE}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelComparison}
            variant="outlined"
            color="secondary"
          >
            {strings.CANCEL}
          </Button>
          <Tooltip
            title={""}
            arrow
            placement="top"
          >
            <span>
              <Button
                onClick={handleRequestComparison}
                variant="contained"
                color="primary"
                disabled={false}
              >
                {strings.BANK_STATEMENT_GENERATE_COMPARISON_TITLE}
                <CreditCost cost={documentComparisonCreditCost()} />
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const StatementSidebar = ({ classes, currentDoc, previousDoc, viewingPrevious, onSelect, canCompare, onCompareClick, onUpload, uploading, creditCosts }) => {
  const currentVerdict = deriveStatementVerdict(currentDoc, previousDoc)
  const previousVerdict = previousDoc ? deriveStatementVerdict(previousDoc, null) : null
  const periodsCount = previousDoc ? 2 : 1

  const documentAnalysisCreditCost = () => findCreditCost(creditCosts, 'DOCUMENT_ANALYSIS')

  const renderCard = (doc, verdict, isPrevious, labelStrings) => {
    const statusStyle = STATUS_STYLES[verdict.statusLabel] || STATUS_STYLES.HEALTHY
    const isActive = viewingPrevious === isPrevious
    const pct = doc.startingBalance !== 0
      ? ((doc.endBalance - doc.startingBalance) / doc.startingBalance) * 100
      : 0
    return (
      <div
        className={`${classes.statementItem} ${isActive ? classes.statementItemActive : ''}`}
        onClick={() => onSelect(isPrevious)}
      >
        <div style={{ flex: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography style={{ fontWeight: 600, fontSize: 13 }}>{labelStrings}</Typography>
            <Typography style={{ fontSize: 11, fontWeight: 700, color: statusStyle.accent }}>{verdict.statusLabel}</Typography>
          </Box>
          <Typography style={{ fontWeight: 700, fontSize: 15 }}>
            {formatCurrency(doc.endBalance)}
            <span style={{ fontSize: 11, fontWeight: 600, color: pct >= 0 ? 'var(--status-approved)' : 'var(--status-pending)', marginLeft: 6 }}>
              {pct >= 0 ? '↑' : '↓'} {Math.abs(pct).toFixed(1)}%
            </span>
          </Typography>
          <Typography style={{ fontSize: 11, color: 'var(--neutral-400)' }}>
            {`Health ${(doc.bankNotesInsightsData?.healthScore || 0).toFixed(1)} / 10`}
          </Typography>
          <div className={classes.statementBar}>
            <div
              className={classes.statementBarFill}
              style={{ width: `${((doc.bankNotesInsightsData?.healthScore || 0) / 10) * 100}%`, backgroundColor: statusStyle.accent }}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.sidebar}>
      <Paper className={classes.sidebarCard}>
        <div className={classes.sidebarHeader}>
          <div className={classes.sidebarHeaderLeft}>
            <Typography style={{ fontWeight: 700, fontSize: 16 }}>{strings.BANK_STATEMENT_STATEMENTS}</Typography>
            <Typography className={classes.statLabel}>{periodsCount} {strings.BANK_STATEMENT_PERIODS_SUFFIX}</Typography>
          </div>
          <div className={classes.sidebarActions}>
            <button
              type="button"
              disabled={!canCompare}
              onClick={onCompareClick}
              style={{
                backgroundColor: canCompare ? '#9b8afb' : '#EDEBFB',
                color: canCompare ? '#fff' : '#b3a9f5',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 16px',
                fontWeight: 600,
                fontSize: 13,
                cursor: canCompare ? 'pointer' : 'not-allowed',
                whiteSpace: 'nowrap'
              }}
            >
              {strings.BANK_STATEMENT_COMPARE_BUTTON}
            </button>
            <input
              accept=".pdf,.jpg,.jpeg,.png,.csv"
              style={{ display: 'none' }}
              id="bank-statement-upload"
              type="file"
              onChange={onUpload}
            />
            <label htmlFor="bank-statement-upload">
              <span
                role="button"
                tabIndex={0}
                style={{
                  backgroundColor: '#fff',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: uploading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                  opacity: uploading ? 0.6 : 1
                }}
              >
                <CloudUploadIcon style={{ fontSize: 16 }} />
                {uploading ? strings.BANK_STATEMENT_UPLOADING : strings.BANK_STATEMENT_UPLOAD_BUTTON} <CreditCost cost={documentAnalysisCreditCost()} />
              </span>
            </label>
          </div>
        </div>

        <div className={classes.statementsRow}>
          {previousDoc && renderCard(previousDoc, previousVerdict, true, `${strings.BANK_STATEMENT_PREVIOUS} · ${formatPeriodLabel(previousDoc.date)}`)}
          {renderCard(currentDoc, currentVerdict, false, `${strings.BANK_STATEMENT_CURRENT} · ${formatPeriodLabel(currentDoc.date)}`)}
          {!previousDoc && (
            <div className={classes.statementItemEmpty}>{strings.BANK_STATEMENT_NO_STATEMENTS_MESSAGE}</div>
          )}
        </div>
      </Paper>
    </div>
  )
}

StatementInsights.propTypes = {
  applicationId: PropTypes.string,
  creditCosts: PropTypes.array.isRequired
}

StatementSidebar.propTypes = {
  classes: PropTypes.object.isRequired,
  currentDoc: PropTypes.object.isRequired,
  previousDoc: PropTypes.object,
  viewingPrevious: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  canCompare: PropTypes.bool.isRequired,
  onCompareClick: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool.isRequired,
  creditCosts: PropTypes.array.isRequired
}

export default StatementInsights
